import { useState, useEffect, useCallback, useMemo } from 'react';
import QRCode from 'qrcode';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import { authHeaders, toGridSortField, toODataSortField } from '../listPageStyles';
import { useC360List, C360Table } from '../c360';
import { Pagination, AccessDeniedNotice } from '../ui';
import { Section } from './DetailShell';
import '../securityScreens.css';

// KULLANICI_YETKI_YONETIMI §9 / §10.4.2 — Kullanıcı kartının güvenlik tarafı sekmeleri.
//
// Bunlar TENANT ayarı DEĞİLDİR (§10.4): kişiden kişiye değişirler, o yüzden Genel Parametreler'e
// konulamazlar. Tenant geneli eşikler (kilit süresi, 2FA modu, şifre yaşı) orada; burada o
// eşiklerin bu kullanıcıdaki SONUCU ve kişiye özel aksiyonlar var.

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('tr-TR') : '—');
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('tr-TR') : '—');
const mb = (v) => (v == null ? null : v >= 1024 ? `${(v / 1024).toFixed(1)} GB` : `${v} MB`);

const PLATFORMS = { 1: 'Web', 2: 'Android', 3: 'iOS', 4: 'Servis' };
// Giriş olayı etiketleri artık rozet tonuyla birlikte LOGIN_EVENT_TYPES'ta (c360 listesi kullanır).
const RIGHT_TYPES = { 1: 'Menü', 2: 'Aksiyon', 3: 'Kolon', 4: 'Veri kapsamı', 5: 'Rol ataması' };

async function pull(url) {
  const res = await fetch(url, { headers: authHeaders() });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(body?.message || body?.Message || 'İstek başarısız');
    // Durum kodu KORUNUR: çağıran taraf "yetkin yok" (403) ile "istek patladı"yı ayırabilsin.
    // Eskiden hepsi tek tip Error'a düşüyor, ekran boş kalıyordu (bkz. AccessDeniedNotice).
    err.status = res.status;
    throw err;
  }
  return body?.data ?? body?.Data ?? body?.value ?? body;
}

/**
 * G-L-01 / G-L-07 / G-K-05 — Güvenlik sekmesi.
 * Hesap kilidi durumu, kilidi açma, son giriş, şifre işlemleri.
 */
export function UserSecurityTab({ userId, canEdit, onToast, currentUserId, isAdmin }) {
  const [policy, setPolicy] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [logins, setLogins] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, a] = await Promise.all([
        pull('/api/auth/password/policy').catch(() => null),
        pull(`/api/auth/sessions?userId=${userId}`).catch(() => []),
        pull(`/api/Users/${userId}/audit?take=25`).catch(() => null),
      ]);
      setPolicy(p);
      setSessions(Array.isArray(s) ? s : []);
      setLogins(a?.logins || []);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Kilit durumu doğrudan bir uçtan gelmiyor; giriş günlüğündeki son "Kilitlendi" olayı ile
  // sonraki başarılı giriş karşılaştırılarak çıkarılıyor. Kesin durum sunucudadır — buradaki
  // gösterim bilgilendirme amaçlıdır, "Kilidi Aç" her hâlükârda güvenle çağrılabilir.
  const lastLock = logins.find((l) => l.eventType === 4);
  const lastLogin = logins.find((l) => l.eventType === 1);
  const looksLocked = lastLock && (!lastLogin || new Date(lastLock.timestamp) > new Date(lastLogin.timestamp));

  const unlock = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/Users/${userId}/unlock`, { method: 'POST', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kilit açılamadı');
      onToast?.('success', 'Hesap kilidi açıldı');
      load();
    } catch (e) {
      onToast?.('error', e.message || 'Kilit açılamadı');
    } finally {
      setBusy(false);
    }
  };

  const revokeAll = async () => {
    setBusy(true);
    try {
      // KK-14: yetki değişikliği oturumu kesmez. Bu buton YETKİ için değil, hesabın ele geçirildiği
      // şüphesi gibi acil durumlar içindir — o yüzden kullanıcı kartında ayrı bir aksiyon.
      await Promise.all(sessions.map((s) =>
        fetch(`/api/auth/sessions/${s.id}`, { method: 'DELETE', headers: authHeaders() })));
      onToast?.('success', `${sessions.length} oturum sonlandırıldı`);
      load();
    } catch (e) {
      onToast?.('error', e.message || 'Oturumlar sonlandırılamadı');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="sec-card"><div className="sec-card-hint">Yükleniyor…</div></div>;

  return (
    <div className="dps-form">
      <Section icon="shield" title="Hesap Güvenliği" subtitle="Kilit durumu ve son hareketler">
        <div className="sec-cards">
          <div className={`sec-card${looksLocked ? ' sec-card--alert' : ''}`}>
            <div className="sec-card-label">Hesap durumu</div>
            <div style={{ marginTop: 8 }}>
              <span className={`sec-badge sec-badge--${looksLocked ? 'danger' : 'ok'}`}>
                {looksLocked ? 'KİLİTLİ' : 'AÇIK'}
              </span>
            </div>
            {looksLocked && <div className="sec-card-hint" style={{ marginTop: 6 }}>{fmtDateTime(lastLock.timestamp)}</div>}
            {canEdit && (
              <button type="button" className="dps-btn ghost" style={{ marginTop: 10 }} disabled={busy} onClick={unlock}>
                Kilidi Aç
              </button>
            )}
          </div>

          <div className="sec-card">
            <div className="sec-card-label">Son giriş</div>
            <div className="sec-card-value" style={{ fontSize: 15 }}>{fmtDateTime(lastLogin?.timestamp)}</div>
            <div className="sec-card-hint">{lastLogin ? `${PLATFORMS[lastLogin.platform] || '—'} · ${lastLogin.ipAddress || '—'}` : 'Kayıt yok'}</div>
          </div>

          <div className="sec-card">
            <div className="sec-card-label">Açık oturum</div>
            <div className="sec-card-value">{sessions.length}</div>
            {canEdit && sessions.length > 0 && (
              <button type="button" className="dps-btn ghost" style={{ marginTop: 8 }} disabled={busy} onClick={revokeAll}>
                Tümünü sonlandır
              </button>
            )}
          </div>
        </div>
      </Section>

      <Section icon="lock" title="Şifre Politikası" subtitle="Bu tenant için geçerli kurallar">
        <dl className="sec-spec">
          <dt>Şifre geçerlilik süresi</dt>
          <dd>{policy?.expiryDays > 0 ? `${policy.expiryDays} gün` : 'Süresiz'}</dd>
          <dt>Tekrar edilemeyen şifre</dt>
          <dd>{policy?.historyCount > 0 ? `Son ${policy.historyCount}` : 'Kontrol kapalı'}</dd>
          <dt>Kilitleme eşiği</dt>
          <dd>{policy?.maxFailedAttempts > 0 ? `${policy.maxFailedAttempts} deneme → ${policy.lockoutMinutes} dk kilit` : 'Kilitleme kapalı'}</dd>
          <dt>İki faktörlü doğrulama</dt>
          <dd>{{ OFF: 'Kapalı', OPTIONAL: 'Opsiyonel', REQUIRED: 'Zorunlu' }[policy?.twoFactorMode] || '—'}</dd>
        </dl>
        <div className="sec-card-hint" style={{ marginTop: 10 }}>
          Bu değerler tenant genelidir ve Sistem Yönetimi › Genel Parametreler ekranından değiştirilir.
          SSO ile giren kullanıcılara şifre kuralları uygulanmaz — şifreleri SSO sağlayıcısındadır.
        </div>
      </Section>

      <TwoFactorPanel
        userId={userId}
        // Authenticator yalnız kişinin KENDİ cihazına kurulabilir; yönetici başkası adına kuramaz.
        isSelf={String(userId) === String(currentUserId)}
        isAdmin={isAdmin}
        twoFactorMode={policy?.twoFactorMode}
        onToast={onToast}
      />

      {/* G-K-11 — KVKK altyapısı vardı ama hiçbir ekrana bağlı değildi; panel o boşluğu kapatır. */}
      <UserKvkkPanel userId={userId} isAdmin={isAdmin} onToast={onToast} />
    </div>
  );
}

/**
 * G-L-07 — İki faktörlü doğrulama (TOTP) kurulum paneli.
 *
 * Akış bilerek üç adımlıdır: setup secret üretir ama 2FA'yı AÇMAZ; kullanıcı QR'ı okuyup ilk doğru
 * kodu üretince açılır. Tek adımda açmak, QR'ı okuyamamış kullanıcıyı sistemden kilitlerdi.
 *
 * `isSelf` false ise (yönetici başkasının kartına bakıyor) yalnız durum gösterilir ve kapatma
 * sunulur — bir yöneticinin başkası adına authenticator kurması zaten mümkün değil.
 */
function TwoFactorPanel({ userId, isSelf, isAdmin, twoFactorMode, onToast }) {
  const [enabled, setEnabled] = useState(null);
  const [setup, setSetup] = useState(null);      // { secret, uri }
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  // Mevcut durumu kullanıcı denetim izinden değil, doğrudan kurulum denemesiyle öğrenemeyiz —
  // bu yüzden durum bilgisi üst bileşenden (policy + kullanıcı kaydı) gelmelidir. Basit tutmak
  // için ilk yüklemede null bırakılır ve kullanıcı aksiyon aldıkça netleşir.
  useEffect(() => { setEnabled(null); setSetup(null); setQrDataUrl(null); setCode(''); }, [userId]);

  const startSetup = async () => {
    setBusy(true);
    try {
      const res = await fetch('/api/auth/2fa/setup', { method: 'POST', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || '2FA kurulumu başlatılamadı');
      const data = body?.data || body?.Data;
      setSetup(data);
      // QR yerel olarak üretilir — secret hiçbir harici servise gönderilmez.
      setQrDataUrl(await QRCode.toDataURL(data.uri, { width: 200, margin: 1 }));
      setEnabled(false);
    } catch (e) {
      onToast?.('error', e.message || '2FA kurulumu başlatılamadı');
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const res = await fetch('/api/auth/2fa/verify', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ code }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kod doğrulanamadı');
      onToast?.('success', 'İki faktörlü doğrulama açıldı');
      setEnabled(true);
      setSetup(null);
      setQrDataUrl(null);
      setCode('');
    } catch (e) {
      onToast?.('error', e.message || 'Kod doğrulanamadı');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const payload = isSelf ? { code } : { userId: Number(userId) };
      const res = await fetch('/api/auth/2fa/disable', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || '2FA kapatılamadı');
      onToast?.('success', 'İki faktörlü doğrulama kapatıldı');
      setEnabled(false);
      setCode('');
    } catch (e) {
      onToast?.('error', e.message || '2FA kapatılamadı');
    } finally {
      setBusy(false);
    }
  };

  const required = String(twoFactorMode).toUpperCase() === 'REQUIRED';
  const off = String(twoFactorMode).toUpperCase() === 'OFF';

  return (
    <Section icon="shield" title="İki Faktörlü Doğrulama" subtitle="Authenticator uygulaması (TOTP)">
      {off && (
        <div className="sec-card-hint" style={{ marginBottom: 10 }}>
          Bu tenant için iki faktörlü doğrulama <strong>kapalı</strong>. Açmak için
          Genel Parametreler › Kimlik Doğrulama ayarını değiştirin.
        </div>
      )}

      {!isSelf ? (
        <>
          <div className="sec-card-hint" style={{ marginBottom: 10 }}>
            Başka bir kullanıcı adına authenticator kurulamaz. Kullanıcı cihazını kaybettiyse
            2FA'sını buradan kapatabilir, kullanıcı kendi kartından yeniden kurabilir.
          </div>
          {isAdmin && (
            <button type="button" className="dps-btn ghost" disabled={busy} onClick={disable}>
              Kullanıcının 2FA'sını Kapat
            </button>
          )}
        </>
      ) : enabled === true ? (
        <>
          <span className="sec-badge sec-badge--ok">AÇIK</span>
          {required ? (
            <div className="sec-card-hint" style={{ marginTop: 10 }}>
              Bu tenant için 2FA zorunludur; kendi hesabınızda kapatamazsınız.
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 12 }}>
              <span className="gp-num">
                <input type="text" inputMode="numeric" maxLength={6} placeholder="6 haneli kod"
                  value={code} onChange={(e) => setCode(e.target.value)} />
              </span>
              <button type="button" className="dps-btn ghost" disabled={busy || code.length !== 6} onClick={disable}>
                Kapat
              </button>
            </div>
          )}
        </>
      ) : setup ? (
        <>
          <div className="sec-card-hint" style={{ marginBottom: 12 }}>
            QR kodu authenticator uygulamanızla (Google/Microsoft Authenticator) okutun, ardından
            uygulamanın ürettiği 6 haneli kodu girin. <strong>Kod doğrulanana kadar 2FA açılmaz.</strong>
          </div>
          {qrDataUrl && <img src={qrDataUrl} alt="2FA QR kodu" style={{ display: 'block', marginBottom: 12 }} />}
          <div className="sec-card-hint" style={{ marginBottom: 12 }}>
            QR okutamıyorsanız anahtarı elle girin: <code className="c360-erp-chip">{setup.secret}</code>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span className="gp-num">
              <input type="text" inputMode="numeric" maxLength={6} placeholder="6 haneli kod"
                value={code} onChange={(e) => setCode(e.target.value)} />
            </span>
            <button type="button" className="dps-btn primary" disabled={busy || code.length !== 6} onClick={verify}>
              {busy ? 'Doğrulanıyor…' : 'Doğrula ve Aç'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="sec-card-hint" style={{ marginBottom: 10 }}>
            TOTP internet bağlantısı olmadan çalışır — saha koşulları için SMS'ten daha güvenilirdir.
          </div>
          <button type="button" className="dps-btn primary" disabled={busy || off} onClick={startSetup}>
            {busy ? 'Hazırlanıyor…' : 'İki Faktörlü Doğrulamayı Kur'}
          </button>
        </>
      )}
    </Section>
  );
}

/**
 * G-L-15 (§7.16 erişim yolu 1) — Cihazlar sekmesi.
 * "Temsilci arıyor, uygulama donuyor" senaryosunun ilk durağı.
 */
export function UserDevicesTab({ userId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await pull(`/api/Users/${userId}/devices`);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) return <div className="sec-card"><div className="sec-card-hint">Yükleniyor…</div></div>;

  return (
    <div className="dps-form">
      <Section icon="box" title="Cihazlar" subtitle="Bu kullanıcının giriş yaptığı cihazlar">
        {rows.length === 0 ? (
          <div className="sec-card-hint">
            Bu kullanıcı henüz mobil uygulamadan giriş yapmamış. Cihaz künyesi ilk girişte
            otomatik oluşur; kullanıcıdan hiçbir bilgi istenmez.
          </div>
        ) : (
          <table className="c360-table" style={{ width: '100%' }}>
            <thead>
              <tr><th>Kısa kod</th><th>Cihaz</th><th>OS</th><th>RAM</th><th>Boş depolama</th>
                <th>Uygulama</th><th>Giriş</th><th>Son görülme</th></tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.deviceKey}>
                  {/* DeviceKey sistem içi bağ anahtarıdır; destek personeline ShortCode gösterilir. */}
                  <td><span className="c360-erp-chip">{d.shortCode}</span></td>
                  <td>{d.modelDisplayName || d.model || '—'}<div className="sec-card-hint">{d.manufacturer}</div></td>
                  <td>{`${d.osName || ''} ${d.osVersion || ''}`.trim() || '—'}</td>
                  <td>{mb(d.totalRamMb) || '—'}</td>
                  <td>{mb(d.availableStorageMb) || '—'}</td>
                  <td>{d.appVersion || '—'}</td>
                  <td>{d.loginCount}</td>
                  <td>{fmtDateTime(d.lastSeen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

/**
 * G-K-04 — Ön Değerler sekmesi. Varsayılan depo, distribütör, belge serisi, açılış ekranı.
 */
export function UserDefaultsTab({ userId, canEdit, onToast }) {
  const KEYS = [
    { key: 'WAREHOUSE', label: 'Varsayılan depo' },
    { key: 'DISTRIBUTOR', label: 'Varsayılan distribütör' },
    { key: 'DOC_SERIES', label: 'Varsayılan belge serisi' },
    { key: 'HOME_SCREEN', label: 'Açılış ekranı' },
  ];

  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await pull(`/api/Users/${userId}/defaults`);
      const map = {};
      (Array.isArray(rows) ? rows : []).forEach((r) => { map[r.defaultKey] = r.defaultValue; });
      setValues(map);
    } catch {
      setValues({});
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(values)
        .filter(([, v]) => v != null && String(v).trim() !== '')
        .map(([k, v]) => ({ defaultKey: k, defaultValue: String(v).trim(), platform: 0 }));
      const res = await fetch(`/api/Users/${userId}/defaults`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kaydedilemedi');
      onToast?.('success', 'Ön değerler kaydedildi');
      load();
    } catch (e) {
      onToast?.('error', e.message || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="sec-card"><div className="sec-card-hint">Yükleniyor…</div></div>;

  return (
    <div className="dps-form">
      <Section icon="sliders" title="Ön Değerler" subtitle="Formların bu kullanıcı için önceden dolacağı değerler">
        <div className="sec-card-hint" style={{ marginBottom: 12 }}>
          Ön değer bir <strong>yetki değildir</strong>: kullanıcının o depoyu veya distribütörü görme
          hakkı Veri Yetkileri sekmesinden gelir. Kapsam dışı bir değer girilirse form onu önerir ama
          listede yine kapsam kuralı geçerli olur.
        </div>
        {KEYS.map((k) => (
          <div key={k.key} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <span className="sec-card-label" style={{ minWidth: 190 }}>{k.label}</span>
            <span className="gp-num">
              <input
                type="text"
                value={values[k.key] || ''}
                disabled={!canEdit}
                placeholder="—"
                onChange={(e) => setValues((v) => ({ ...v, [k.key]: e.target.value }))}
              />
            </span>
          </div>
        ))}
        {canEdit && (
          <button type="button" className="dps-btn primary" style={{ marginTop: 8 }} disabled={saving} onClick={save}>
            {saving ? 'Kaydediliyor…' : 'Ön Değerleri Kaydet'}
          </button>
        )}
      </Section>
    </div>
  );
}

/**
 * G-K-11 — KVKK paneli. Rıza durumu ve kişisel veri hakları kullanıcı kartında.
 *
 * <p>Bu ürünün KVKK altyapısı zaten vardı (8 rıza tipi, veri silme, dışa aktarma uçları) ama
 * hiçbir ekrana bağlı DEĞİLDİ — yani pratikte kullanılamıyordu. Panel o boşluğu kapatır.</p>
 */
const CONSENT_LABELS = {
  EXPLICIT_CONSENT: 'Açık rıza (kişisel verilerin işlenmesi)',
  PRIVACY_NOTICE_ACK: 'Aydınlatma metni okundu onayı',
  MARKETING_SMS: 'SMS ile pazarlama izni',
  MARKETING_EMAIL: 'E-posta ile pazarlama izni',
  MARKETING_CALL: 'Telefon ile pazarlama izni',
  PROFILING: 'Profilleme amacıyla işleme izni',
  THIRD_PARTY_SHARING: 'Üçüncü taraflarla paylaşım izni',
  SENSITIVE_DATA_PROCESSING: 'Özel nitelikli veri işleme izni',
};

export function UserKvkkPanel({ userId, isAdmin, onToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await pull(`/api/Users/${userId}/consents`));
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const exportData = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/Users/${userId}/personal-data/export`, { headers: authHeaders() });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || body?.Message || 'Dışa aktarılamadı');
      }
      // Veri taşıma hakkı: kullanıcıya makine-okur bir dosya verilmeli, ekranda göstermek yetmez.
      const blob = new Blob([JSON.stringify(await res.json(), null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kisisel-veri_${userId}_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      onToast?.('success', 'Kişisel veri dosyası indirildi');
    } catch (e) {
      onToast?.('error', e.message || 'Dışa aktarılamadı');
    } finally {
      setBusy(false);
    }
  };

  const deletePersonalData = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/Users/${userId}/personal-data`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Silinemedi');
      onToast?.('success', 'Kişisel veriler anonimleştirildi');
      setConfirmDelete(false);
      load();
    } catch (e) {
      onToast?.('error', e.message || 'Silinemedi');
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="sec-card"><div className="sec-card-hint">Yükleniyor…</div></div>;

  const byType = Object.fromEntries((data?.consents || []).map((c) => [c.consentType, c]));

  return (
    <Section icon="shield" title="KVKK / Kişisel Veriler" subtitle="Rıza durumu ve veri hakları">
      {data?.personalDataDeleted && (
        <div className="sec-card sec-card--alert" style={{ marginBottom: 12 }}>
          <div className="sec-card-label">Kişisel veriler silindi</div>
          <div className="sec-card-hint" style={{ marginTop: 5 }}>
            {fmtDate(data.personalDataDeletedAt)} tarihinde anonimleştirildi. Bu işlem geri alınamaz.
          </div>
        </div>
      )}

      <table className="c360-table" style={{ width: '100%' }}>
        <thead><tr><th>Rıza türü</th><th style={{ width: 130 }}>Durum</th><th style={{ width: 150 }}>Tarih</th><th style={{ width: 120 }}>IP</th></tr></thead>
        <tbody>
          {Object.entries(CONSENT_LABELS).map(([key, label]) => {
            const c = byType[key];
            return (
              <tr key={key}>
                <td>{label}</td>
                <td>
                  {!c
                    ? <span className="sec-badge sec-badge--muted">Alınmamış</span>
                    : c.isGranted
                      ? <span className="sec-badge sec-badge--ok">Verildi</span>
                      : <span className="sec-badge sec-badge--danger">İptal edildi</span>}
                </td>
                <td>{c ? fmtDate(c.isGranted ? c.consentDate : c.revokedAt) : '—'}</td>
                <td>{c?.ipAddress || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: 8, marginTop: 13, flexWrap: 'wrap' }}>
        <button type="button" className="dps-btn ghost" disabled={busy} onClick={exportData}>
          Kişisel Veriyi Dışa Aktar
        </button>
        {isAdmin && !data?.personalDataDeleted && (
          <button type="button" className="dps-btn danger" disabled={busy} onClick={() => setConfirmDelete(true)}>
            Kişisel Verileri Sil
          </button>
        )}
      </div>

      {confirmDelete && (
        <div className="sec-card sec-card--alert" style={{ marginTop: 12 }}>
          <div className="sec-card-label">Silme onayı</div>
          <div className="sec-card-hint" style={{ marginTop: 6, lineHeight: 1.55 }}>
            Ad, soyad, e-posta, telefon ve şifreli kimlik bilgileri <strong>anonimleştirilecek</strong>.
            Bu işlem <strong>geri alınamaz</strong> ve kullanıcı bir daha giriş yapamaz.
            Belge geçmişi ve denetim izi korunur.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" className="dps-btn danger" disabled={busy} onClick={deletePersonalData}>
              {busy ? 'Siliniyor…' : 'Evet, Anonimleştir'}
            </button>
            <button type="button" className="dps-btn ghost" onClick={() => setConfirmDelete(false)}>İptal</button>
          </div>
        </div>
      )}
    </Section>
  );
}

/**
 * G-K-09 — Hareketler sekmesi. Yetki değişiklikleri ve giriş olayları tek zaman çizelgesinde.
 */
export function UserActivityTab({ userId }) {
  const [data, setData] = useState({ permission: [] });
  const [loading, setLoading] = useState(true);
  // Sunucu bu kullanıcının denetim izini göstermeyi reddetti (403) — boş listeden AYRI durum.
  const [denied, setDenied] = useState(false);

  // Yetki değişiklikleri hâlâ tek atışta gelir: bu kaynak KULLANICI EYLEMİYLE değil YETKİ
  // DEĞİŞİKLİĞİYLE büyür (yılda birkaç düzine satır) — sayfalamaya değecek bir hacmi yok.
  // Giriş hareketleri ise her oturum açılışında büyür; o yüzden ayrı, sayfalı bir listedir.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await pull(`/api/Users/${userId}/audit?take=100`);
        if (!cancelled) { setData({ permission: d?.permission || [] }); setDenied(false); }
      } catch (e) {
        // 403 = yetki yok; diğer hatalarda eski davranış (boş liste) korunur.
        if (!cancelled) { setData({ permission: [] }); setDenied(e?.status === 403); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) return <div className="sec-card"><div className="sec-card-hint">Yükleniyor…</div></div>;

  // Yetkisizlik TÜM sekmeyi kapsar: iki bölüm de aynı kurala tabidir (id != kendisi && !admin).
  // Boş tablo yerine ne olduğunu söyleyen tek bir bildirim gösterilir — talep: "yanlış anlaşılıp
  // hata açılıyor".
  if (denied) {
    return (
      <div className="dps-form">
        <AccessDeniedNotice detail="Kullanıcı hareketleri yalnızca yöneticiler ve kaydın sahibi tarafından görüntülenebilir." />
      </div>
    );
  }

  return (
    <div className="dps-form">
      <Section icon="shield" title="Yetki Değişiklikleri" subtitle="Bu kullanıcıya verilen ve alınan haklar">
        {data.permission.length === 0 ? (
          <div className="sec-card-hint">Yetki değişikliği kaydı yok.</div>
        ) : (
          <table className="c360-table" style={{ width: '100%' }}>
            <thead><tr><th>Zaman</th><th>Yapan</th><th>Hak tipi</th><th>Hak</th><th>Eski</th><th>Yeni</th></tr></thead>
            <tbody>
              {data.permission.map((p, i) => (
                <tr key={i}>
                  <td>{fmtDateTime(p.timestamp)}</td>
                  <td>#{p.actor}</td>
                  <td>{RIGHT_TYPES[p.rightType] || p.rightType}</td>
                  <td><span className="c360-erp-chip">{p.detail}</span></td>
                  <td>{p.oldValue || '—'}</td>
                  <td>{p.newValue
                    ? <span className={`sec-badge sec-badge--${p.newValue === 'VAR' ? 'ok' : 'danger'}`}>{p.newValue}</span>
                    : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <UserLoginAuditSection userId={userId} />
    </div>
  );
}

// ── Giriş Hareketleri — c360 listesi ────────────────────────────────────────────────────────
//
// Eskiden bu bölüm `/{id}/audit` ucundan gelen ilk 100 satırı DÜZ bir <table> olarak basıyordu:
// sayfalama, kolon filtresi ve sıralama yoktu. Giriş kaydı her oturum açılışında büyüyen tek
// kaynak olduğu için tablo zamanla hem uç hem tarayıcı tarafında ağırlaşıyor, üstelik 100.
// satırdan eskisi hiç görünmüyordu (sessiz kesme — kullanıcı "kayıt yok" sanıyordu).
//
// Artık sistem standardı: useC360List + C360Table + Pagination, `UserId eq {id}` baseFilter'ı ile.
// Tablo yalnız gördüğü sayfayı çeker.

const LOGIN_EVENT_TYPES = {
  1: { label: 'Giriş', tone: 'ok' },
  2: { label: 'Çıkış', tone: 'muted' },
  3: { label: 'Başarısız', tone: 'warn' },
  4: { label: 'Kilitlendi', tone: 'danger' },
  5: { label: 'Token yenileme', tone: 'muted' },
  6: { label: 'Şifre değişimi', tone: 'info' },
};

// ResultCode loga yazılan AYRINTILI sebeptir; kullanıcıya dönen mesaj her zaman tekildir (G-L-02).
// Denetim görünümü olduğu için burada gerçek sebep gösterilir. LoginAuditPage ile aynı sözlük.
const LOGIN_RESULT_CODES = {
  INVALID_PASSWORD: 'Şifre hatalı',
  USER_NOT_FOUND: 'Kullanıcı bulunamadı',
  LOCKED: 'Hesap kilitli',
  INACTIVE: 'Hesap pasif',
  CANCELLED: 'Hesap iptal',
  LICENSE_LIMIT: 'Lisans sınırı',
  LOGIN_WINDOW_CLOSED: 'Giriş saati dışında',
  LOCAL_LOGIN_DISABLED: 'Yerel giriş kapalı',
  PASSWORD_EXPIRED: 'Şifre süresi doldu',
  SINGLE_SESSION: 'Tek oturum kuralı',
  CLIENT_VERSION_TOO_OLD: 'Eski istemci sürümü',
};

// Serbest metin araması yalnız string OData alanları üzerinde (int/enum'a contains yapılmaz).
const LOGIN_SEARCH_FIELDS = ['ResultCode', 'IpAddress', 'ClientVersion'];

const LOGIN_COLUMN_FILTER_META = {
  timestamp: { type: 'date', odataField: 'Timestamp' },
  eventType: {
    type: 'select', odataField: 'EventType',
    options: Object.entries(LOGIN_EVENT_TYPES).map(([v, o]) => ({ value: v, label: o.label })),
  },
  resultCode: { type: 'text', odataField: 'ResultCode' },
  platform: {
    type: 'select', odataField: 'Platform',
    options: Object.entries(PLATFORMS).map(([v, l]) => ({ value: v, label: l })),
  },
  clientVersion: { type: 'text', odataField: 'ClientVersion' },
  ipAddress: { type: 'text', odataField: 'IpAddress' },
};

function UserLoginAuditSection({ userId }) {
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selected, setSelected] = useState(null);

  const list = useC360List({
    apiBase: `/api/Users/${userId}/login-audit`,
    columnFilterMeta: LOGIN_COLUMN_FILTER_META,
    searchFields: LOGIN_SEARCH_FIELDS,
    defaultSortField: 'Timestamp',
    defaultSortDir: 'desc',   // günlükte en yeni kayıt en üstte
    // Kullanıcı yeni açılmışsa (isNew) sekme zaten çizilmez; yine de id yoksa istek atma.
    ready: !!userId,
  });

  const {
    rows, totalCount, loading, forbidden, columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
  } = list;

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('timestamp', {
      header: 'Zaman', meta: { filterType: 'date' }, cell: (i) => fmtDateTime(i.getValue()),
    }),
    columnHelper.accessor('eventType', {
      header: 'Olay', meta: { filterType: 'select' },
      cell: (i) => {
        const e = LOGIN_EVENT_TYPES[i.getValue()] || { label: '—', tone: 'muted' };
        return <span className={`sec-badge sec-badge--${e.tone}`}>{e.label}</span>;
      },
    }),
    columnHelper.accessor('resultCode', {
      header: 'Sonuç', meta: { filterType: 'text' },
      cell: (i) => { const v = i.getValue(); return v ? (LOGIN_RESULT_CODES[v] || v) : '—'; },
    }),
    columnHelper.accessor('platform', {
      header: 'Platform', meta: { filterType: 'select' }, cell: (i) => PLATFORMS[i.getValue()] || '—',
    }),
    // Girişi yapan istemcinin sürümü — mobilde kurulu APK (ör. 0.0.9-rc.174). Sürüm bildirmeyen
    // eski istemcilerde boştur.
    columnHelper.accessor('clientVersion', {
      header: 'Sürüm', meta: { filterType: 'text' },
      cell: (i) => { const v = i.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '—'; },
    }),
    columnHelper.accessor('ipAddress', { header: 'IP', meta: { filterType: 'text' }, cell: (i) => i.getValue() || '—' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('Timestamp', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  return (
    <Section icon="user" title="Giriş Hareketleri" subtitle="Giriş/çıkış olayları — sayfalı">
      {forbidden ? (
        // "Kayıt yok" ile "yetkin yok" ayrımı: ikincisinde boş tablo göstermek arıza sanılıyordu.
        <AccessDeniedNotice detail="Giriş hareketleri yalnızca yöneticiler ve kaydın sahibi tarafından görüntülenebilir." />
      ) : (
      <>
      <C360Table
        table={table}
        columnFilterMeta={LOGIN_COLUMN_FILTER_META}
        columnFilters={columnFilters}
        onApplyFilter={setColumnFilter}
        onClearFilter={clearColumnFilter}
        loading={loading}
        selected={selected}
        onRowClick={setSelected}
        emptyText="Giriş kaydı yok."
      />
      <Pagination
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={pageSize}
        rowCount={rows.length}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
      />
      </>
      )}
    </Section>
  );
}

export { fmtDate };
